import { useState } from 'react';
import { motion } from 'motion/react';
import {
  Ship,
  Plane,
  Truck,
  TrainFront,
  Container as ContainerIcon,
  MapPin,
  Calendar,
  ChevronRight,
  Trash2,
  Pencil,
  Coins,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import type {
  ImportOrderLeg,
  TransportMode,
  ImportLegStatus,
} from '../types/import-tracking.types';
import { LEG_STATUS_LABEL } from '../types/import-tracking.types';
import { LegPricingDialog } from './leg-pricing-dialog';

const MODE_ICON: Record<
  TransportMode,
  { icon: React.ElementType; emoji: string; gradient: string }
> = {
  SEA: { icon: Ship, emoji: '🚢', gradient: 'from-blue-500/20 to-cyan-500/10' },
  AIR: { icon: Plane, emoji: '✈️', gradient: 'from-sky-500/20 to-indigo-500/10' },
  LAND: { icon: Truck, emoji: '🚛', gradient: 'from-amber-500/20 to-orange-500/10' },
  RAIL: { icon: TrainFront, emoji: '🚂', gradient: 'from-slate-500/20 to-zinc-500/10' },
  MULTIMODAL: { icon: ContainerIcon, emoji: '📦', gradient: 'from-purple-500/20 to-pink-500/10' },
};

const STATUS_TONE: Record<ImportLegStatus, string> = {
  PENDING: 'bg-muted text-muted-foreground border-border',
  IN_PROGRESS: 'bg-primary/10 text-primary border-primary/30 animate-pulse',
  COMPLETED: 'bg-ok-suave text-ok border-ok-borde',
  DELAYED: 'bg-bloqueo-suave text-bloqueo border-bloqueo-borde',
  CANCELLED: 'bg-muted text-muted-foreground border-border line-through',
};

interface LegProgressCardProps {
  leg: ImportOrderLeg;
  onAdvance?: (legId: string) => void;
  onEdit?: (legId: string) => void;
  onDelete?: (legId: string) => void;
  onPricingUpdated?: () => void;
  isReadonly?: boolean;
  /**
   * Por qué la orden no deja mover este tramo, o `null` si sí deja.
   *
   * Lo calcula el padre a partir del estado de la orden — la tarjeta sola no lo
   * sabe. Cuando viene con texto, el botón **no se dibuja** y en su lugar queda
   * el motivo: un botón que existe pero da error enseña menos que no ofrecerlo.
   */
  motivoBloqueo?: string | null;
}

function formatDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('es-BO', { day: '2-digit', month: 'short' });
}

function formatMoney(v: number | string | null | undefined, currency = 'USD'): string {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return '';
  return `${currency} ${n.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function LegProgressCard({
  leg,
  onAdvance,
  onEdit,
  onDelete,
  onPricingUpdated,
  isReadonly,
  motivoBloqueo,
}: LegProgressCardProps) {
  const [pricingOpen, setPricingOpen] = useState(false);
  const mode = MODE_ICON[leg.transportMode] ?? MODE_ICON.SEA;
  const Icon = mode.icon;
  const canAdvance =
    !isReadonly &&
    !motivoBloqueo &&
    !!onAdvance &&
    (leg.status === 'PENDING' || leg.status === 'IN_PROGRESS');
  const calculatedAmount = formatMoney(leg.calculatedAmount, leg.currency ?? 'USD');

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'relative rounded-xl border-2 p-4 bg-gradient-to-br',
        mode.gradient,
        leg.status === 'IN_PROGRESS' && 'border-primary shadow-md shadow-primary/20',
        leg.status === 'COMPLETED' && 'border-ok-borde',
        leg.status === 'PENDING' && 'border-border',
        leg.status === 'DELAYED' && 'border-bloqueo-borde'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="text-3xl">{mode.emoji}</div>
          <div>
            <div className="flex items-center gap-2 text-sm font-bold">
              <span>{leg.origin}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <span>{leg.destination}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Icon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Tramo {leg.legOrder}</span>
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase',
                  STATUS_TONE[leg.status]
                )}
              >
                {LEG_STATUS_LABEL[leg.status]}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {canAdvance && (
            <Button size="sm" variant="default" onClick={() => onAdvance?.(leg.id)}>
              {leg.status === 'PENDING' ? 'Iniciar' : 'Completar'}
            </Button>
          )}
          {/*
            El motivo ocupa el lugar del botón, no se suma a él.

            Solo aparece si el tramo estaba en condiciones de moverse: en uno ya
            completado no hay nada que explicar, y repetir el aviso en los seis
            tramos de una orden en borrador es ruido.
          */}
          {!isReadonly &&
            motivoBloqueo &&
            (leg.status === 'PENDING' || leg.status === 'IN_PROGRESS') && (
              <span className="rounded-md border border-dashed border-border bg-background/60 px-2 py-1 text-[11px] text-muted-foreground">
                {motivoBloqueo}
              </span>
            )}
          {!isReadonly && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setPricingOpen(true)}
              aria-label={`Configurar pricing del tramo ${leg.legOrder}`}
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
            >
              <Coins className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">Precio</span>
            </Button>
          )}
          {!isReadonly && onEdit && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onEdit(leg.id)}
              aria-label={`Editar tramo ${leg.legOrder}: ${leg.origin} → ${leg.destination}`}
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
            >
              <Pencil className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">Editar</span>
            </Button>
          )}
          {!isReadonly && onDelete && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onDelete(leg.id)}
              aria-label={`Eliminar tramo ${leg.legOrder}`}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          <span className="text-muted-foreground">Esperado:</span>
          <span className="font-medium">
            {formatDate(leg.expectedStart)} → {formatDate(leg.expectedEnd)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          <span className="text-muted-foreground">Real:</span>
          <span className="font-medium">
            {formatDate(leg.actualStart)} → {formatDate(leg.actualEnd)}
          </span>
        </div>
        {leg.carrier && (
          <div className="flex items-center gap-2 col-span-2 bg-white/60 dark:bg-foreground/5 rounded-md px-2.5 py-1.5 border border-border/50">
            <MapPin className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
              Proveedor:
            </span>
            <span className="font-bold text-sm">{leg.carrier}</span>
            {leg.trackingRef && (
              <span className="ml-auto text-xs text-muted-foreground">
                Ref: <span className="font-mono text-foreground">{leg.trackingRef}</span>
              </span>
            )}
          </div>
        )}
        {calculatedAmount && (
          <div className="flex items-center gap-1.5 col-span-2">
            <Coins className="w-3 h-3 text-muted-foreground" />
            <span className="text-muted-foreground">Costo:</span>
            <span className="font-semibold text-ok">{calculatedAmount}</span>
          </div>
        )}
      </div>

      <LegPricingDialog
        open={pricingOpen}
        onOpenChange={setPricingOpen}
        leg={leg}
        onSaved={() => {
          setPricingOpen(false);
          onPricingUpdated?.();
        }}
      />
    </motion.div>
  );
}
